import { autoinject, transient } from "aurelia-framework";
import { NotifyPropertyChangedBase } from "../../../utilities/reflection";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { Field } from '../../components/queries/field'
import { GridUtils, EditableFieldBinding } from "../data-grid/data-grid";
import { EntityMetaDataModel, EntityFilterPropertyModel, DataType, EntityProjectionPropertyModel, QueryFilter } from "../../../models/queries/queries.models";
import { BindingSignaler } from "aurelia-templating-resources";
import { QueriesClientService } from "../../../services/queries/queries-client.services";
import { NotificationClientService, NotificationType } from "../../../services/basics/notification-client.service";
import { DateHelper } from "../../../utilities/date-helper";
import { Guid } from "../../../utilities/guid";
import { I18N } from "aurelia-i18n";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";

@autoinject
@transient()
export class EditPropertiesDialog extends NotifyPropertyChangedBase {

    public properties: EntityFilterPropertyModel[] = [];
    public fields: Field[] = [];
    private selectedIds: number[] = [];
    private toggleAll: boolean = false;
    private filters: QueryFilter[] = [];
    private model: EntityMetaDataModel;
    private reload: () => void;

    constructor(private dialogClient: DialogClientService,
        private bindingSignaler: BindingSignaler,
        private notificationService: NotificationClientService,
        private queryService: QueriesClientService,
        private i18N: I18N) {
        super();
    }

    activate(model: any) {
        this.model = model.model;
        this.properties = this.model.filterProperties.filter(x => x.isUpdatable === true);
        this.selectedIds = model.selectedIds;
        this.reload = model.reload;
        this.toggleAll = model.toggleAll;
        this.filters = model.filters;
        this.fields.push({ id: Guid.generateGuid(), property: null, value: "" })
    }

    public onPropertySelected(field: Field): void {
        field.value = undefined;
        setTimeout(() => {
            this.bindingSignaler.signal("update-filters-html");
        });
    }

    change(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this.hasEmptyFields()) {
                this.notificationService.show(this.i18N.tr('empty-fields'), this.i18N.tr('empty-fields-description-edit-properties'), NotificationType.Warning, 5000);
                resolve();
            }

            //used so that lookup keeps same value after values are stringified
            const fields = CloneHelper.cloneObject(this.fields);

            this.castValuesToString(fields);
            this.queryService.updateODataEntities(this.model.uri, this.selectedIds, JSON.stringify(this.filters), this.toggleAll, fields)
                .then(x => {
                    if (x.isSuccess) {
                        this.reload();
                        this.notificationService.show(this.i18N.tr('successful-properties-change'), this.i18N.tr('successful-properties-change-description'), NotificationType.Success, 5000);
                        this.dialogClient.hide();
                    } else {
                        let error: string = '';
                        if (x.messages) {
                            x.messages.forEach(message => {
                                error += message += '</br>';
                            });
                        }

                        this.notificationService.show(this.i18N.tr('error-text'), error ? error : this.i18N.tr('no-fields-to-edit'), NotificationType.Error, 5000);
                    }
                    resolve();
                })
                .catch(() => {
                    this.notificationService.show(this.i18N.tr('error-text'), this.i18N.tr('error-save-edit-properties'), NotificationType.Error, 5000);
                    reject();
                });
        });
    }

    hasEmptyFields(): boolean {
        let empty: boolean = false;

        this.fields.forEach(x => {
            const property = this.properties.filter(y => y.name == x.property)[0];

            if (x.property == null || (!property.isNullable && !x.value)) {
                empty = true;
            }
        });

        return empty;
    }

    castValuesToString(fields: Field[]) {
        fields.forEach(x => {
            const property = this.properties.filter(y => y.name == x.property)[0];
            //checking if property is a date then changing it to json to solve formatting issues with backend
            if (property.type == 12 || property.type == 7 && x.value) {
                x.value = DateHelper.castDate(x.value).toJSON();
            } else if (typeof x.value != "string" && x.value != null) {
                x.value = x.value.toString();
            }
        });
    }

    cancel() {
        this.dialogClient.hide();
    }

    addField() {
        this.fields.push({ id: Guid.generateGuid(), property: null, value: "" });
    }

    delete(field: Field) {
        this.fields = this.fields.filter(x => x.id != field.id);
    }

    public getLookupMappedToProperty(field: Field) {
        const filter: EntityFilterPropertyModel = field == null || field.property == null ? this.properties[0] : this.properties.filter(x => x.name == field.property)[0];
        return filter ? filter.lookupMappedToProperty : '';
    }

    public filtersHtml(field: Field): string {

        if (!field) {
            return '';
        }

        const filter: EntityFilterPropertyModel = field == null || field.property == null ? this.properties[0] : this.properties.filter(x => x.name == field.property)[0];

        if (!filter) {
            return '';
        }

        const projectedProperty = this.model && this.model.projectionProperties
            ? this.model.projectionProperties.filter(x => x.name == filter.name)[0]
            : null;

        const binding: EditableFieldBinding = {
            query: "lookupMappedToProperty",
            lookup: this.getLookupMappedToProperty(field),
            isEditable: filter.isUpdatable,
            binding: "field.value",
            displayValueBinding: "${field.value}",
            value: "lookupMappedToProperty",
            isValueCollection: false,
            isPropertyCollection: filter.isCollection,
            dataType: filter.lookupMappedToProperty && filter.type != DataType.HierarchicalLookup ? DataType.Lookup : filter.type,
            parameter: "field.value",
            secondParameter: this.getSecondParameter(filter, projectedProperty),
            isNotRangeDatePicker: true,
            isSecondParameterBindable: false,
            startLabel: filter.startLabel,
            endLabel: filter.endLabel,
            isNullable: filter.isNullable
        };

        return GridUtils.getControl(binding, true);
    }

    private getSecondParameter(filter: EntityFilterPropertyModel, projectedProperty: EntityProjectionPropertyModel) {

        if (filter.type == DataType.Lookup || filter.type == DataType.HierarchicalLookup) {
            return filter.lookupParametersMappedToProperty;
        }

        if (!projectedProperty) {
            return "";
        }
        const type = filter.type;
        return type == DataType.Date || type == DataType.DateTime ? projectedProperty.format : "";
    }
}
